import { useEffect, useState } from 'react';

import { limuloPollInterval } from '@/lib/limulo/polling';
import { NO_POLL_TRACKING, trackPollOutcome, type PollTracking } from '@/lib/pollingBackoff';

/**
 * Le opzioni di polling da stendere nella chiamata di una query Limulo:
 * `useGetXQuery(arg, { skip, ...polling.ritmo })`.
 *
 * 🔴 **`skipPollingIfUnfocused: true` e' la meta' che non si vede.** Con la pagina nascosta (app
 * in secondo piano, Telegram chiuso sopra la WebView) RTK Query salta il giro: il timer resta, la
 * richiesta no — letto in `startNextPoll` di `rtk-query.modern.mjs` (2.11.2): `if
 * (state.config.focused || !skipPollingIfUnfocused) dispatch(refetchQuery(...))`. E
 * `state.config.focused` lo tiene `setupListeners` (`store/index.ts`) su `visibilitychange`.
 * ⚠️ Al ritorno non si aspetta il giro: `refetchOnFocus` (acceso su tutto `limuloApi`) rilegge
 * SUBITO. Il polling governa solo chi guarda senza toccare.
 */
export interface LimuloRitmo {
  readonly pollingInterval: number;
  readonly skipPollingIfUnfocused: true;
}

/** Cio' che serve di una query per contare l'esito del giro (v. `lib/pollingBackoff.ts`). */
export interface LimuloGiro {
  readonly startedTimeStamp?: number;
  readonly fulfilledTimeStamp?: number;
  readonly isError: boolean;
}

export interface LimuloPollingStato {
  readonly base: number;
  readonly tracking: PollTracking;
}

export interface LimuloPolling {
  readonly ritmo: LimuloRitmo;
  /** Uso interno di `useLimuloPollingGuida`. */
  readonly aggiorna: (fn: (prev: LimuloPollingStato) => LimuloPollingStato) => void;
}

/**
 * Il ritmo di una schermata Limulo: **due hook, uno prima e uno dopo la query**, e la ragione e'
 * un cerchio che non si puo' chiudere in un render solo.
 *
 * Il ritmo dipende da DUE cose che si sanno solo DOPO aver chiamato la query: il suo esito (un
 * fallimento rallenta, una risposta buona azzera — `lib/pollingBackoff.ts`) e, sul dettaglio, lo
 * STATO del torneo che la query stessa porta (terminale ⇒ zero). Ma il ritmo va passato ALLA
 * query. ⇒ Lo stato sta qui, e `useLimuloPollingGuida` lo aggiorna in un effetto dopo il render:
 * vale dal giro seguente. E' la stessa forma di `routes/Tournaments.tsx` (`pollTracking`), non
 * un'invenzione di questo file.
 *
 * ⚠️ **Si parte da ZERO, non dal ritmo vivo**: finche' la prima risposta non dice se il torneo e'
 * vivo, non si arma nessun timer. Il primo giro lo fa comunque la sottoscrizione; l'effetto arriva
 * subito dopo il montaggio, quindi il ritardo e' un render, non un intervallo.
 */
export function useLimuloPolling(): LimuloPolling {
  const [stato, setStato] = useState<LimuloPollingStato>({ base: 0, tracking: NO_POLL_TRACKING });
  return {
    ritmo: {
      pollingInterval: limuloPollInterval(stato.base, stato.tracking.streak),
      skipPollingIfUnfocused: true,
    },
    aggiorna: setStato,
  };
}

/**
 * Dopo la query: registra l'esito del giro e il ritmo di base per il giro seguente.
 *
 * @param polling il valore di `useLimuloPolling` della stessa schermata
 * @param guida   la query di cui si conta l'esito — quella che il ritmo governa. Se il ritmo e'
 *   condiviso da piu' query della stessa fonte (torneo + iscrizione), basta la principale: sono
 *   lo stesso BFF, e cadono insieme.
 * @param base    il ritmo con la fonte sana; `0` = non si interroga a ritmo (torneo terminale,
 *   pagina oltre la prima). ⚠️ Un fallimento su una base zero NON riaccende niente: v.
 *   `limuloPollInterval`.
 *
 * 🔴 L'esito si legge dal TENTATIVO (`startedTimeStamp`/`fulfilledTimeStamp`), mai dallo stato
 * cumulativo: il perche', misurato nel sorgente di RTK, sta per intero in `lib/pollingBackoff.ts`.
 */
export function useLimuloPollingGuida(polling: LimuloPolling, guida: LimuloGiro, base: number): void {
  const { aggiorna } = polling;
  const { startedTimeStamp, fulfilledTimeStamp, isError } = guida;
  useEffect(() => {
    aggiorna((prev) => {
      const tracking = trackPollOutcome(prev.tracking, { startedTimeStamp, fulfilledTimeStamp, isError });
      // ⚠️ Stesso oggetto se niente e' cambiato: React salta il render, e un effetto che
      // riscrive lo stato a ogni passaggio sarebbe un ciclo.
      return tracking === prev.tracking && base === prev.base ? prev : { base, tracking };
    });
  }, [aggiorna, startedTimeStamp, fulfilledTimeStamp, isError, base]);
}
